<template>
  <div class="app">
    <header class="app-bar">
      <UiContainer class="app-bar__inner">
        <!-- Мобильная версия -->
        <template v-if="isMobile">
          <UiButton
            variant="secondary"
            class="app-bar__burger"
            @click="showMobileMenu = true"
            aria-label="Меню"
          >
            ☰
          </UiButton>
          <router-link to="/" class="app-bar__logo">
            <img src="/logo.svg" alt="ЛабАктив" width="28" height="28" />
            <span>ЛабАктив</span>
          </router-link>
          <UiButton
            v-if="!isSupplier"
            :to="'/cart'"
            variant="secondary"
            class="app-bar__icon-btn"
            aria-label="Корзина"
          >
            <span class="app-bar__badge-wrap">
              🛒
              <UiBadge v-if="cartItemsCount > 0" variant="accent" class="app-bar__badge">{{
                cartItemsCount
              }}</UiBadge>
            </span>
          </UiButton>
          <UiButton
            :to="'/favorites'"
            variant="secondary"
            class="app-bar__icon-btn"
            aria-label="Избранное"
          >
            <span class="app-bar__badge-wrap">
              ♡
              <UiBadge v-if="favoritesCount > 0" variant="accent" class="app-bar__badge">{{
                favoritesCount
              }}</UiBadge>
            </span>
          </UiButton>
        </template>

        <!-- Десктоп -->
        <template v-else>
          <router-link to="/" class="app-bar__logo app-bar__logo--desktop">
            <img src="/logo.svg" alt="ЛабАктив" width="40" height="40" />
            <div>
              <span class="app-bar__logo-title">ЛабАктив</span>
              <span class="app-bar__logo-sub">Снабжение и закупки</span>
            </div>
          </router-link>

          <div class="app-bar__search">
            <svg
              class="app-bar__search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              v-model="searchQuery"
              type="search"
              class="app-bar__input"
              placeholder="Поиск товаров..."
              aria-label="Поиск"
              data-tutorial="search-input"
              @keyup.enter="performSearch"
            />
          </div>

          <AppNavActions
            :is-authenticated="isAuthenticated"
            :is-admin="isAdmin"
            :is-supplier="isSupplier"
            :is-buyer="isBuyer"
            :is-supplier-showcase-enabled="isSupplierShowcaseEnabled"
            :is-procurement-enabled="isProcurementEnabled"
            :is-chat-enabled="isChatEnabled"
            :user-name="user?.name ?? ''"
            :cart-items-count="cartItemsCount"
            :favorites-count="favoritesCount"
            @logout="logout"
          />
        </template>
      </UiContainer>
    </header>

    <AppMobileDrawer
      v-model="showMobileMenu"
      v-model:search-query="searchQuery"
      :is-authenticated="isAuthenticated"
      :is-admin="isAdmin"
      :is-supplier="isSupplier"
      :is-buyer="isBuyer"
      :is-supplier-showcase-enabled="isSupplierShowcaseEnabled"
      :is-procurement-enabled="isProcurementEnabled"
      :is-chat-enabled="isChatEnabled"
      :favorites-count="favoritesCount"
      :unread-notifications-count="unreadNotificationsCount"
      @search="performSearchAndClose"
      @logout="logout"
    />

    <main class="app-main">
      <router-view />
    </main>

    <AppFooter
      :is-supplier-showcase-enabled="isSupplierShowcaseEnabled"
      :is-procurement-enabled="isProcurementEnabled"
      :is-buyer="isBuyer"
      :is-supplier="isSupplier"
      :is-admin="isAdmin"
    />

    <UiSnackbar
      :model-value="showNotification"
      :message="notificationMessage"
      :type="notificationColor"
      :action-label="notificationActionLabel ?? ''"
      @update:model-value="dismissNotification"
      @action="onNotificationAction"
    />
    <UiSnackbar
      :model-value="snackbar.show"
      :message="snackbar.message"
      :type="snackbarType"
      :action-label="snackbar.action?.label ?? ''"
      @update:model-value="hideSnackbar"
      @action="onSnackbarAction"
    />

    <AuthPromptModal />
    <HelpCenter />
    <ContextualHelp />
    <TutorialOverlay />
    <OnboardingWizard v-if="isAuthenticated && shouldShowOnboarding" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AuthPromptModal } from '@/modules/common/public'
import { ContextualHelp, HelpCenter } from '@/modules/help/public'
import { OnboardingWizard } from '@/modules/onboarding/public'
import { TutorialOverlay, useTutorial } from '@/modules/tutorial/public'
import { useAnalytics } from '@/modules/analytics/public'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiSnackbar from '@/shared/ui/UiSnackbar.vue'
import AppNavActions from '@/app/components/AppNavActions.vue'
import AppMobileDrawer from '@/app/components/AppMobileDrawer.vue'
import AppFooter from '@/app/components/AppFooter.vue'
import { useAppShell } from '@/app/composables/useAppShell'
import { BREAKPOINTS } from '@/constants/breakpoints'

const router = useRouter()
const route = useRoute()
const {
  auth,
  cart,
  favorites,
  featureFlags,
  snackbar,
  hideSnackbar,
  unreadNotificationsCount,
  isProcurementEnabled,
  isSupplierShowcaseEnabled,
  isChatEnabled,
  showNotification,
  notificationMessage,
  notificationColor,
  notificationActionLabel,
  initializeShellData,
  dismissNotification,
  onNotificationAction: triggerNotificationAction,
} = useAppShell()
const { shouldShowOnboarding } = useTutorial()
const analytics = useAnalytics()

const searchQuery = ref('')
const showMobileMenu = ref(false)

const isMobile = ref(false)
function checkMobile() {
  isMobile.value = typeof window !== 'undefined' && window.innerWidth < BREAKPOINTS.md
}
if (typeof window !== 'undefined') {
  window.addEventListener('resize', checkMobile)
  checkMobile()
}

const isAuthenticated = computed(() => auth.isAuthenticated.value)
const isBuyer = computed(() => auth.isBuyer.value)
const isSupplier = computed(() => auth.isSupplier.value)
const user = computed(() => auth.user.value)
const cartItemsCount = computed(() => cart.itemsCount.value)
const favoritesCount = computed(() => favorites.favoritesCount.value)
const snackbarType = computed(() => {
  const color = snackbar.value.color

  return color === 'success' || color === 'error' || color === 'warning' || color === 'info'
    ? color
    : 'info'
})
const isAdmin = computed(() => auth.isAdmin.value)

function onNotificationAction() {
  triggerNotificationAction((to) => router.push(to))
}

function onSnackbarAction() {
  const action = snackbar.value.action
  if (action?.to) {
    router.push(action.to)
  } else if (action?.onClick) {
    action.onClick()
  }
  hideSnackbar()
}

watch(
  computed(() => featureFlags.isEnabled('analytics_enabled')),
  (enabled) => {
    if (enabled) {
      analytics.grantAnalyticsConsent()
      analytics.initializeAnalytics().catch(console.error)
    } else {
      analytics.denyAnalyticsConsent()
    }
  },
  { immediate: true },
)

onMounted(async () => {
  await initializeShellData(isAuthenticated.value)

  // Guard не восстанавливает сессию на гостевых маршрутах, поэтому прямой заход на /login
  // или /register с живой cookie-сессией показывает форму входа залогиненному пользователю
  if (isAuthenticated.value && route.meta['requiresGuest']) {
    await router.replace({ name: 'home' })
  }
})

function performSearch() {
  if (searchQuery.value.trim())
    router.push({ path: '/catalog', query: { q: searchQuery.value.trim() } })
}
function performSearchAndClose() {
  if (searchQuery.value.trim()) {
    router.push({ path: '/catalog', query: { q: searchQuery.value.trim() } })
    showMobileMenu.value = false
  }
}
async function logout() {
  await auth.signOut()
  showMobileMenu.value = false
  router.push('/')
}
</script>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  min-height: 64px;
  background: var(--color-brand-strong);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
}

.app-bar__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 16px;
  padding-right: 16px;
}

.app-bar__burger {
  flex-shrink: 0;
}

.app-bar__logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #fff;
  font-weight: 600;
}

.app-bar__logo--desktop {
  gap: 12px;
  min-width: 220px;
}

.app-bar__logo-title,
.app-bar__logo-sub {
  display: block;
}

.app-bar__logo-sub {
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
}

.app-bar__search {
  position: relative;
  flex: 1;
  max-width: 420px;
  margin: 0 12px;
}

.app-bar__search-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.app-bar__input {
  width: 100%;
  height: 40px;
  padding: 8px 12px 8px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font: inherit;
  box-sizing: border-box;
}

.app-bar__icon-btn {
  padding: 8px;
}

.app-bar__badge-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
}

.app-bar__badge-wrap :deep(.app-bar__badge) {
  position: absolute;
  top: -8px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  box-sizing: border-box;
  pointer-events: none;
}

/* Тёмный фон шапки: инвертируем кнопки для контраста */
.app-bar :deep(.ui-button--primary) {
  background: #fff;
  border-color: #fff;
  color: var(--color-brand-strong);
}

.app-bar :deep(.ui-button--primary:hover:not(:disabled)) {
  background: #e2e8f0;
  border-color: #e2e8f0;
}

.app-bar :deep(.ui-button--secondary) {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
}

.app-bar :deep(.ui-button--secondary:hover:not(:disabled)) {
  background: rgba(255, 255, 255, 0.12);
  border-color: #fff;
  color: #fff;
}

@media (max-width: 1260px) {
  .app-bar__logo-sub {
    display: none;
  }

  .app-bar__search {
    max-width: 320px;
    margin: 0 8px;
  }
}

.app-main {
  flex: 1;
}

@media (max-width: 600px) {
  .app-bar {
    height: 56px;
    min-height: 56px;
  }
}
</style>
